import { useNavigate, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import PageContainer from '../components/PageContainer';
import { useEffect, useRef, useState } from 'react';
import Loader from '../components/Loader';
import { useDispatch } from 'react-redux';
import { setBacklink } from '../redux/backlinkSlice';
import { useTranslation } from 'react-i18next';
import { addProductToCart } from '../redux/cartSlice';
import CartButton from '../components/shopOrders/CartButton';
import { useContentLinkClick } from '../components/ExternalLink';
import { getCloudinaryDisplayUrl } from '../utils/getCloudinaryDisplayUrl';

const ProductDetails = () => {
    const { t } = useTranslation();

    // Handler delegato per i link nella descrizione (interni → SPA, esterni → browser in-app / nuova scheda)
    const handleContentLinkClick = useContentLinkClick();

    // get id param
    const [searchParams] = useSearchParams();
    const productId = searchParams.get('productId');

    // Imposto backlink su Shop
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(setBacklink("/shop"))
    }, [dispatch])

    // get product Data
    const [productData, setProductData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (productId) {
            getProductData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId])
    const getProductData = async () => {
        setIsLoading(true);
        const token = localStorage.getItem('ticino-wild-token');
        const url = `${process.env.REACT_APP_SERVER_BASE_URL}/products/get-product/${productId}`;
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        const options = { method: 'GET', headers };
        try {
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setProductData(result.payload);
                setError(null);
                setIsLoading(false);
            } else {
                const error = await response.json();
                throw error
            }
        } catch (error) {
            setError(error);
            setProductData(null);
            setIsLoading(false);
        }
    }

    // Handle selected photo (Big photo)
    const [selectedPhoto, setSelectedPhoto] = useState(null)
    useEffect(() => {
        if (productData) {
            // Variante w_1600: il secure_url originale può pesare diversi MB
            setSelectedPhoto(getCloudinaryDisplayUrl(productData.gallery[0].secure_url))
        }
    }, [productData])
    const selectPhoto = (index) => {
        setSelectedPhoto(getCloudinaryDisplayUrl(productData.gallery[index].secure_url))
    }



    // Gestisco le informazioni da mandare in backend per l'acquisto: productId, size, quantity
    const [purchaseData, setPurchaseData] = useState({
        productId,
        title: null,
        coverImageUrl: null,
        size: null,
        quantity: null,
        price: null,
        shippingCost: null
    });
    useEffect(() => {
        if (productData) {
            setPurchaseData(prevState => ({
                ...prevState,
                title: productData.title,
                coverImageUrl: productData.coverImageUrl.resizedUrl,
                price: productData.price,
                shippingCost: productData.shippingCost
            }))
        }
    }, [productData])
    // Gestisco selezione size e quantity
    const handleSizeAndQuantity = (event) => {
        const { id, value } = event.target;
        let fixedId = id;
        let fixedValue = value;
        if (id.split('-')[0] === 'size') {
            fixedValue = id.split('-')[1];
            fixedId = 'size';
        }
        setPurchaseData(prevState => ({ ...prevState, [fixedId]: fixedValue }))
    }

    // Aggiungo i prodotti al carrello e azzero gli input
    const navigate = useNavigate();
    const [isAdded, setIsAdded] = useState(false);
    const handleAddToCart = () => {
        dispatch(addProductToCart(purchaseData));
        emptySizeAndQuantity();
        setIsAdded(true)
    }
    const inputRef = useRef();
    const emptySizeAndQuantity = () => {
        inputRef.current.value = ""
        setPurchaseData(prevState => ({
            ...prevState,
            size: null,
            quantity: null
        }))
    }
    useEffect(() => {
        if (isAdded) {
            setTimeout(() => {
                setIsAdded(false)
            }, 2000)
        }
    }, [isAdded])

    // Torno allo shop
    const backToShop = () => {
        navigate("/shop")
    }
    return (
        <PageContainer desktopItems="center">
            {isLoading && <Loader />}
            {error && (error.message || 'Errore sconosciuto nel ricevere i dati del prodotto')}
            {productData && <div className="w-full lg:w-[1000px] flex flex-col lg:flex-row justify-center items-start gap-8">
                {/* PHOTOS */}
                <div className='flex-1 flex flex-col gap-2'>
                    {/* cover */}
                    <div>
                        <img src={selectedPhoto} alt={productData.title} className='rounded-xl' />
                    </div>
                    {/* Other photos */}
                    <div className='flex gap-2 flex-wrap'>
                        {
                            productData.gallery.length > 0 && productData.gallery.map((element, index) => {
                                return <div key={index} className='w-12 h-12' onClick={() => selectPhoto(index)}><img src={element.resizedUrl} alt={productData.title} className='rounded-xl' /></div>
                            })
                        }
                    </div>
                </div>

                {/* INFO */}
                <div className='flex-1 flex flex-col border-l-2 pl-4'>
                    <h2>{productData.title}</h2>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(productData.description) }} onClick={handleContentLinkClick}></div>
                    <div className='text-2xl my-6 text-[#225424]'>{parseFloat(productData.price).toFixed(2)} CHF {productData.shippingCost > 0 && <span className='text-xs text-neutral-500'>+ {parseFloat(productData.shippingCost).toFixed(2)} CHF {t('Homepage.ShopSection.ofShippingcost')}</span>}</div>
                    {
                        productData.size.length > 0 &&
                        <div className="flex flex-col gap-2 my-8">
                            <label htmlFor="size">{t('ProductDetails.selectSize')}</label>
                            <div className="flex gap-1">
                                {
                                    productData.size.map((element, index) => {
                                        return <div key={index} className={`bg-white p-1 rounded-full border-2 cursor-pointer ${String(purchaseData.size) === String(element) ? 'border-[#225424]' : 'border-white'}`}><div id={`size-${element}`} className={`w-12 h-12 rounded-full flex justify-center items-center`} onClick={handleSizeAndQuantity}>{element}</div></div>
                                    })
                                }
                            </div>
                        </div>
                    }

                    <div className='flex flex-col gap-2'>
                        <label htmlFor="quantity">{t('ProductDetails.selectQuantity')}</label>
                        <div className="flex gap-2">
                            <input ref={inputRef} type="number" id="quantity" onChange={handleSizeAndQuantity} className='w-24' placeholder='0' min='0' />
                            <div className='ml-4 flex justify-center items-center min-w-[96px]'>
                                {/* BUTTON Aggiungi al carrello */}
                                {
                                    purchaseData.productId && !isAdded && ((productData.size.length > 0 && purchaseData.size) || (!productData.size || productData.size.length === 0)) && (purchaseData.quantity && String(purchaseData.quantity) !== '0') &&
                                    <button className='bg-[#225424] text-white py-3 px-6 rounded-xl' onClick={handleAddToCart}><i className="fi fi-rr-shopping-cart"></i> {t('ProductDetails.addToCart')}</button>
                                }
                            </div>
                        </div>


                        <div className=''>
                            <h4>
                                <span className='text-neutral-100'>.</span>
                                {isAdded && <span>{t('ProductDetails.addedToCart')}</span>}
                            </h4>
                        </div>
                        <div className='mt-8'>
                            <CartButton />
                            <button className='bg-[#225424] text-white py-3 px-6 rounded-xl' onClick={backToShop}>{t('ProductDetails.viewOtherProducts')}</button>
                        </div>
                    </div>
                </div>
            </div>}

        </PageContainer>
    )
}

export default ProductDetails;